import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Search, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTenants } from '@/hooks/useApi'

interface TenantDropdownProps {
  selectedTenantId: string | null
  onSelectTenant: (tenantId: string) => void
}

export function TenantDropdown({ selectedTenantId, onSelectTenant }: TenantDropdownProps) {
  const { tenants, loading } = useTenants()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  


  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredTenants = tenants.filter(tenant => {
    const tenantId = tenant.tenant_id || (tenant.pk ? tenant.pk.split('#')[0].replace('t_', '') : '')
    const tenantName = tenant.tenant_name || tenant.display_name || tenantId
    return tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           tenantId.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const selectedTenant = tenants.find(t => {
    const tenantId = t.tenant_id || (t.pk ? t.pk.split('#')[0].replace('t_', '') : '')
    return tenantId === selectedTenantId
  })
  const displayName = selectedTenant?.tenant_name || selectedTenant?.display_name || selectedTenantId || 'Select Tenant'

  const handleSelect = (tenantId: string) => {
    onSelectTenant(tenantId)
    setIsOpen(false)
    setSearchTerm('')
    // Dispatch event to notify components of tenant change
    window.dispatchEvent(new CustomEvent('tenant-changed', { detail: { tenantId } }))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="outline" 
        className="justify-between min-w-[200px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {displayName}
        <ChevronDown className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[300px] bg-white border rounded-md shadow-lg z-50">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading tenants...</div>
            ) : filteredTenants.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No tenants found</div>
            ) : (
              filteredTenants.map((tenant) => {
                const tenantId = tenant.tenant_id || (tenant.pk ? tenant.pk.split('#')[0].replace('t_', '') : '')
                const tenantName = tenant.tenant_name || tenant.display_name || tenantId
                const isSelected = selectedTenantId === tenantId
                
                return (
                  <button
                    key={tenantId}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                    onClick={() => handleSelect(tenantId)}
                  >
                    <div>
                      <div className="font-medium">{tenantName}</div>
                      <div className="text-sm text-gray-500">ID: {tenantId}</div>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}